import { useCallback, useEffect, useRef, useState } from 'react'
import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Editor } from 'primereact/editor'
import { InputTextarea } from 'primereact/inputtextarea'
import FormField from '../FormField'
import { resolveFieldError } from '../../../lib/forms/messages'

/** Editöre dışarıdan metin ekletmek için verilen tutamak (parametre paneli bunu kullanır). */
export interface HtmlEditorHandle {
  /** Kodu **imleç konumuna** (imleç yoksa metnin sonuna) düz metin olarak ekler. */
  insert(code: string): void
}

/**
 * Quill örneğinin kullandığımız yüzeyi. PrimeReact `onLoad` tipi `any` verir; burada daraltılır ki
 * bileşen içinde `any` dolaşmasın (FR-007 no-any).
 */
interface QuillLike {
  root: HTMLElement
  getLength(): number
  insertText(index: number, text: string, source?: string): void
  deleteText(index: number, length: number, source?: string): void
  setSelection(index: number, length: number, source?: string): void
}

/**
 * Quill'in belgeye **gerçekten aldığı** HTML. Boş belgede Quill `<p><br></p>` üretir; PrimeReact bunu
 * `null`'a çevirdiği için burada da `''` döndürülür → "boş" durumu tek biçimde temsil edilir.
 */
function quillHtml(q: QuillLike): string {
  if (q.getLength() <= 1) return ''
  const html = q.root.innerHTML
  return html === '<p><br></p>' ? '' : html
}

interface FormHtmlEditorFieldProps<T extends FieldValues> {
  control: Control<T>
  name: FieldPath<T>
  label: string
  required?: boolean
  full?: boolean
  /**
   * **HTML metninin** üst sınırı (DB kolon sınırı). Editörün kendi `maxLength`'i *düz metni* saydığı
   * için kullanılMAZ: sınır kaydedilecek değere, yani etiketler dahil HTML'e uygulanır. Aşım
   * doğrulamayla engellenir; **editörün sağ alt köşesindeki sayaç** kullanıcıyı önceden uyarır.
   * Verilmezse sayaç gösterilmez.
   */
  maxLength?: number
  /** Editör hazır olduğunda (veya kaldırıldığında) tutamağı bildirir. */
  onReady?: (name: string, handle: HtmlEditorHandle | null) => void
}

/**
 * RHF-bağlı **zengin metin (HTML) editörü** — PrimeReact `Editor` (Quill 2) + `Controller` +
 * `FormField`. 059-b: e-posta şablonlarının içeriği HTML'dir.
 *
 * <p><b>Değer sözleşmesi:</b> RHF'de tutulan değer **HTML metnidir**. Editör boşaltıldığında Quill
 * `<p><br></p>` üretir; PrimeReact bunu `null`'a çevirir ve biz `''` yazarız → "boş" durumu tek bir
 * biçimde temsil edilir.
 *
 * <p><b>Ekleme neden Quill üzerinden yapılır:</b> PrimeReact `Editor`, dışarıdan gelen `value`
 * değişimini **yalnız editör odakta değilken** uygular (odaktayken imleci bozmamak için). Dolayısıyla
 * parametre kodunu RHF değerine yazmak, kullanıcı editörün içindeyken **hiçbir şey yapmaz**. Bu yüzden
 * ekleme doğrudan Quill API'siyle (`insertText`) yapılır; Quill'in `text-change` olayı değeri RHF'ye
 * geri akıtır ve imleç eklenen kodun sonuna taşınır.
 *
 * <p><b>Etiket ilişkisi:</b> `label`, Quill'in ürettiği `contenteditable` öğesine bağlanır
 * (`quill.root.id = name`) — PrimeReact'in kök `div`'ine değil. Böylece etiket gerçekten yazılan
 * öğeyi işaret eder.
 *
 * <p><b>HTML kaynağı görünümü:</b> editörün üstündeki düğme, zengin metin ile **ham HTML** arasında
 * geçiş yapar. İki görünüm **aynı RHF değerini** düzenler; kaynakta yazılan HTML geri dönüldüğünde
 * editöre yüklenir. Görünümler aynı anda render edilMEZ — ikisi de `id={name}` taşır (etiket ve
 * parametre panelinin hedef bulması buna dayanır), iki kopya aynı anda dursaydı `id` çakışırdı.
 *
 * <p><b>Gidiş-dönüş kayıplı olabilir (bilinçli):</b> Quill yalnız desteklediği biçimleri temsil eder;
 * kaynak görünümünde yazılan `<table>` gibi markup zengin metne dönüldüğünde **düşer**. Bu kaçınılmazdır
 * (editörün doğası), ama **sessiz değildir**: dönüşte RHF değeri Quill'in gerçekten aldığı hâlle
 * eşitlenir, böylece "gördüğün = kaydedilecek olan" değişmezi korunur ve kayıp o anda (sayaçta ve kaynak
 * görünümünde) görünür. Eşitleme yapılmasa değer ilk tuş vuruşunda sessizce düzleşirdi.
 */
export default function FormHtmlEditorField<T extends FieldValues>({
  control,
  name,
  label,
  required,
  full = true,
  maxLength,
  onReady,
}: Readonly<FormHtmlEditorFieldProps<T>>) {
  const { t } = useTranslation()
  const quill = useRef<QuillLike | null>(null)
  /**
   * Son bilinen imleç aralığı. Quill odağı kaybettiğinde `getSelection()` `null` döner; panel butonuna
   * tıklamak da odağı alır → konum **selection-change anında** saklanmalıdır.
   */
  const range = useRef<{ index: number; length: number } | null>(null)

  /** `true` iken zengin metin editörü yerine ham HTML alanı gösterilir. */
  const [source, setSource] = useState(false)

  /**
   * Kaynak görünümünden zengin metne **dönüşü** işaretler; dönüşte RHF değeri Quill'in aldığı hâlle
   * eşitlenir (bkz. `onLoad`). Yalnız dönüşte yapılır — form ilk açıldığında değere dokunulmaz, aksi
   * halde kayıt yalnızca diyalog açıldığı için "kirli" sayılırdı.
   */
  const syncOnLoad = useRef(false)

  /** Görünümü değiştirir; kaynaktan dönüşte değer eşitlemesi gerektiğini işaretler. */
  const toggleSource = () => {
    if (source) syncOnLoad.current = true
    setSource(!source)
  }

  const insert = useCallback((code: string) => {
    const q = quill.current
    if (!q) return
    // Quill'in uzunluğu daima kapanış '\n'ini içerir → son paragrafın İÇİNE eklemek için -1.
    const end = Math.max(q.getLength() - 1, 0)
    // Kayıtlı konum **kırpılır**: değer dışarıdan değişmiş olabilir (ör. düz metin → HTML yükseltmesi
    // ya da farklı bir kaydın açılması) ve eski aralık artık belgenin dışına düşebilir.
    const at = Math.min(range.current?.index ?? end, end)
    const selected = Math.min(range.current?.length ?? 0, end - at)
    if (selected > 0) q.deleteText(at, selected, 'user')
    q.insertText(at, code, 'user')
    q.setSelection(at + code.length, 0, 'user')
    range.current = { index: at + code.length, length: 0 }
  }, [])

  /** Bileşen kaldırılırken (ör. tür e-postadan çıkınca) tutamak geri alınır — ölü referans kalmaz. */
  useEffect(() => {
    return () => onReady?.(name, null)
    // `onReady` her render'da yeniden üretilebildiği için bağımlılığa alınMAZ; kimlik `name`'dir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name])

  /**
   * Kaynak görünümüne geçilince Quill kaldırılır → **tutamak geri alınır**. Aksi halde parametre paneli
   * artık DOM'da olmayan bir editöre yazmayı denerdi (buton ölü görünürdü); tutamak düşünce panel düz
   * metin yoluna geçer ve kodu `textarea`'nın imleç konumuna ekler. Zengin metne dönüldüğünde Editor
   * yeniden yüklenir ve `onLoad` tutamağı yeniden kaydeder.
   */
  useEffect(() => {
    if (!source) return
    quill.current = null
    range.current = null
    onReady?.(name, null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, name])

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const value = (field.value as string | undefined) ?? ''
        const over = maxLength != null && value.length > maxLength
        return (
          <FormField
            label={label}
            htmlFor={name}
            required={required}
            full={full}
            error={resolveFieldError(fieldState.error?.message, t)}
          >
            {/* Yükseklik ve araç çubuğu görünümü CSS'te (`.ui-html-editor`) — tema ile tutarlı kalır. */}
            <div className="ui-html-editor">
              {/*
                Görünüm düğmesi editörün ÜSTÜNDEDİR: Quill'in kendi araç çubuğuna eklemek `headerTemplate`
                ile tüm çubuğu elle yeniden kurmayı gerektirirdi (varsayılan düğmeler kaybolurdu).
                `aria-pressed` iki durumlu olduğunu bildirir.
              */}
              <div className="ui-html-editor-bar">
                <button
                  type="button"
                  className={`ui-html-source-toggle${source ? ' active' : ''}`}
                  onClick={toggleSource}
                  aria-pressed={source}
                  aria-controls={name}
                >
                  <i className="pi pi-code" aria-hidden="true" />
                  {t('common.htmlSource')}
                </button>
              </div>
              {source ? (
                /*
                  Ham HTML alanı. `maxLength` **verilMEZ**: sınır HTML uzunluğuna uygulanır ama tarayıcı
                  kırpması kullanıcının etiketini yarıda keserdi — aşımı sayaç uyarır, doğrulama engeller
                  (zengin metin görünümündeki davranışın aynısı).
                */
                <InputTextarea
                  id={name}
                  name={field.name}
                  value={value}
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={field.onBlur}
                  rows={10}
                  spellCheck={false}
                  aria-label={label}
                  className={`ui-html-source${fieldState.error ? ' p-invalid' : ''}`}
                />
              ) : (
                <Editor
                  value={value}
                  onTextChange={(e) => field.onChange(e.htmlValue ?? '')}
                  onSelectionChange={(e) => {
                    if (e.range) range.current = { index: e.range.index, length: e.range.length }
                  }}
                  onLoad={(instance: QuillLike) => {
                    quill.current = instance
                    instance.root.id = name
                    instance.root.setAttribute('aria-label', label)
                    onReady?.(name, { insert })
                    /*
                      Kaynak görünümünden dönüşte **görünen ile kaydedilecek değeri eşitle.** Quill,
                      temsil edemediği markup'ı (ör. <table>) belgeye alırken DÜŞÜRÜR ve bunu
                      `setContents` ile, `text-change` dinleyicisi bağlanmadan ÖNCE yapar (PrimeReact
                      `initQuill`). Eşitlenmezse RHF ham HTML'i tutmaya devam eder: ekranda düzleşmiş
                      içerik görünürken kaydedilecek değer başkadır ve **ilk tuş vuruşunda** değer
                      sessizce düzleşmiş sürümle ezilir (kullanıcının tablosu fark edilmeden gider).
                      Burada eşitleyince kayıp anında ve görünür olur: sayaç güncellenir, kaynak
                      görünümüne dönülünce Quill'in kabul ettiği HTML görülür.
                      `onLoad`, `setContents`'ten SONRA tetiklendiği için `root.innerHTML` normalize
                      hâli taşır (doğrulandı: primereact/editor `useUpdateEffect([quillCreated])`).
                    */
                    if (syncOnLoad.current) {
                      syncOnLoad.current = false
                      const shown = quillHtml(instance)
                      if (shown !== value) field.onChange(shown)
                    }
                  }}
                  className={fieldState.error ? 'p-invalid' : undefined}
                />
              )}
              {/*
                Sayaç editörün **sağ alt köşesinde** durur (alan altında ayrı bir satır değil): yazma
                alanının kendi bilgisidir ve formu uzatmaması gerekir. `aria-hidden` + `pointer-events`
                kapalı — bilgilendirici bir süs; sınır aşımını **doğrulama** bildirir, sayaç yalnız
                önceden uyarır.
              */}
              {maxLength != null && (
                <span
                  className={`ui-html-editor-count${over ? ' over' : ''}`}
                  aria-hidden="true"
                >
                  {/* `count` DEĞİL `len`: i18next `count`'u çoğul anahtarı sanar (charCount_other). */}
                  {t('common.charCount', { len: value.length, max: maxLength })}
                </span>
              )}
            </div>
          </FormField>
        )
      }}
    />
  )
}
